import * as CookieConsent from "vanilla-cookieconsent";
import {getHelpScoutConfig, cookieCallbacks as helpScoutCallbacks} from "./HelpScout";
import {getGtmConfig, cookieCallbacks as gtmCallbacks } from "./GoogleTagManager";
import {getSentryConfig, cookieCallbacks as sentryCallbacks} from "./Sentry";
import i18n, { loadLanguage } from "@/plugins/i18n";

const initializeLanguage = async () => {
    try {
        await loadLanguage();
        return true;
    } catch (error) {
        console.error('Błąd podczas ładowania języka w cookie consent:', error);
        return false;
    }
};

const CAT_NECESSARY = "necessary";
const CAT_ANALYTICS = "analytics";
const CAT_ADVERTISEMENT = "advertisement";
const CAT_FUNCTIONALITY = "functionality";
const CAT_PERSONALIZATION = "personalization";
const CAT_SECURITY = "security";


const categories = {
    [CAT_NECESSARY]: {
        enabled: true,
        readOnly: true,
    },
    [CAT_ANALYTICS]: {
        enabled: false,
        readOnly: false,
        services: {}
    },
    [CAT_ADVERTISEMENT]: {
        enabled: false,
        readOnly: false,
        services: {}
    },
    [CAT_FUNCTIONALITY]: {
        enabled: false,
        readOnly: false,
        services: {}
    },
    [CAT_PERSONALIZATION]: {
        enabled: false,
        readOnly: false,
        services: {}
    },
    [CAT_SECURITY]: {
        enabled: false,
        readOnly: false,
        services: {}
    }


};


window.scriptsLoaded = window.scriptsLoaded || {};

function registerService(serviceConfig) {
    const lang = detectLanguage();
    const config = serviceConfig(i18n);
    try {
        categories[config.category].services[config.service] = {
            label: config.config.label,
            autoClear: config.autoClear
        };
    } catch (error) {
        console.error('Error registering service:', error);
    }
}

function detectLanguage() {
    const htmlTag = document.querySelector('html');
    const lang = htmlTag && htmlTag.lang ? htmlTag.lang.toLowerCase() : null;

    const supportedLanguages = ['pl', 'en', 'ua'];

    if (lang && supportedLanguages.includes(lang)) {
        return lang;
    }

    return 'pl';
}


function getPrivacyPolicyUrl(lang) {
    switch (lang) {
        case 'pl':
            return import.meta.env.VITE_PRIVACY_POLICY_URL_PL || '/polityka-prywatnosci';
        case 'en':
            return import.meta.env.VITE_PRIVACY_POLICY_URL_EN || '/privacy-policy';
        case 'ua':
            return import.meta.env.VITE_PRIVACY_POLICY_URL_UK || '/privacy-policy-ua';
        default:
            return import.meta.env.VITE_PRIVACY_POLICY_URL_PL || '/polityka-prywatnosci';
    }
}

function getContactUrl(lang) {
    switch (lang) {
        case 'pl':
            return import.meta.env.VITE_CONTACT_URL || '/kontakt';
        case 'en':
            return import.meta.env.VITE_CONTACT_URL_EN || '/contact';
        case 'ua':
            return import.meta.env.VITE_CONTACT_URL_UK || '/kontakt';
        default:
            return import.meta.env.VITE_CONTACT_URL || '/kontakt';
    }
}

/**
 * Get constants for categories and services
 * @returns {Object} Object containing category and service constants
 */
function getConstants() {
    return {
        categories: {
            NECESSARY: CAT_NECESSARY,
            ANALYTICS: CAT_ANALYTICS,
            ADVERTISEMENT: CAT_ADVERTISEMENT,
            FUNCTIONALITY: CAT_FUNCTIONALITY,
            PERSONALIZATION: CAT_PERSONALIZATION,
            SECURITY: CAT_SECURITY
        }
    };
}

/**
 * Get all service callbacks
 * @returns {Array} Array of service callbacks
 */
function getServiceCallbacks() {
    return [
        gtmCallbacks,
        sentryCallbacks,
        helpScoutCallbacks
    ];
}
function executeServiceCallbacks(eventType, cookie, changedCategories = [], changedServices = {}) {
    const callbacks = getServiceCallbacks();
    const utils = {getConstants};

    callbacks.forEach(serviceCallbacks => {
        if (serviceCallbacks && typeof serviceCallbacks[eventType] === 'function') {
            if (eventType === 'onChange') {
                serviceCallbacks[eventType](cookie, changedCategories, changedServices, utils);
            } else {
                serviceCallbacks[eventType](cookie, utils);
            }
        }
    });
}

document.addEventListener('DOMContentLoaded', async function () {
    await initializeLanguage();
    const lang = detectLanguage()

    // Rejestracja usług
    registerService(getHelpScoutConfig);
    registerService(getGtmConfig);
    registerService(getSentryConfig);

    window.CookieConsent = CookieConsent
    window.cookieconsent = CookieConsent.run({
        root: document.body,
        autoClearCookies: true,
        language: {
            default: import.meta.env.VITE_COOKIE_CONSENT_DEFAULT_LANGUAGE || lang,
            translations: {
                [lang]: {
                    consentModal: {
                        title: i18n.t('tekst Używamy plików cookie (cookie consent)'),
                        description: i18n.t('tekst Witaj! Ta strona używa plików cookie i podobnych technologii, aby zapewnić prawidłowe działanie, analizować ruch i personalizować treści. Kliknij "Akceptuj wszystkie" aby wyrazić zgodę na używanie wszystkich plików cookie, lub wybierz "Dostosuj ustawienia" aby zdecydować, które pliki cookie mogą być używane. (cookie consent)'),
                        acceptAllBtn:  i18n.t('tekst Akceptuj wszystkie (cookie consent)'),
                        acceptNecessaryBtn:  i18n.t('tekst Odrzuć wszystkie (cookie consent)'),
                        showPreferencesBtn:  i18n.t('tekst Dostosuj ustawienia (cookie consent)'),
                        closeIconLabel:  i18n.t('tekst Zamknij (cookie consent)'),
                        footer: '<a href="' + getPrivacyPolicyUrl('pl') + '" target="_blank">'+i18n.t('tekst Polityka prywatności (cookie consent)')+'</a>'
                    },
                    preferencesModal: {
                        title: i18n.t('tekst Ustawienia plików cookie (cookie consent)'),
                        acceptAllBtn: i18n.t('tekst Akceptuj wszystkie (cookie consent)'),
                        acceptNecessaryBtn: i18n.t('tekst Odrzuć wszystkie (cookie consent)'),
                        savePreferencesBtn: i18n.t('tekst Zapisz ustawienia (cookie consent)'),
                        closeIconLabel: i18n.t('tekst Zamknij (cookie consent)'),
                        serviceCounterLabel: i18n.t('tekst Usługi (cookie consent)'),
                        sections: [
                            {
                                title: i18n.t('tekst Wykorzystanie plików cookie (cookie consent)'),
                                description: i18n.t('tekst Używamy plików cookie, aby zapewnić podstawowe funkcje witryny i poprawić Twoje doświadczenia online. (cookie consent)')
                            },
                            {
                                title: i18n.t('tekst Niezbędne pliki cookie (cookie consent)'),
                                description: i18n.t('tekst Te pliki cookie i podobne technologie są wykorzystywane do działań, które są ściśle niezbędne do prowadzenia obsługi lub świadczenia zażądanej przez użytkownika usługi. Nie wymagają one zgody i nie można ich dezaktywować. (cookie consent)'),
                                linkedCategory: CAT_NECESSARY
                            },
                            {
                                title: i18n.t('tekst Analityka (cookie consent)'),
                                description: i18n.t('tekst Pliki cookie używane do analityki pomagają zbierać dane, które pozwalają usługom zrozumieć, w jaki sposób użytkownicy wchodzą w interakcję z określoną usługą. Te informacje pozwalają usługom zarówno na ulepszanie treści, jak i na budowanie lepszych funkcji, które poprawiają doświadczenie użytkownika. (cookie consent)'),
                                linkedCategory: CAT_ANALYTICS
                            },
                            {
                                title: i18n.t('tekst Reklama (cookie consent)'),
                                description: i18n.t('tekst Te pliki cookies pozwalają na odpowiednie dostosowanie treści reklamowych do zainteresowań Użytkownika, nie tylko na naszym Portalu, ale także poza nią. Reklamowe pliki cookies mogą być instalowane za pośrednictwem Portalu, również przez podmioty współpracujące z Administratorem. Na podstawie informacji z tych plików cookies oraz aktywności w innych witrynach internetowych, budowany jest profil zainteresowań Użytkownika. Celem jest wyświetlanie reklam, które są istotne i angażujące dla poszczególnych Użytkowników. (cookie consent)'),
                                linkedCategory: CAT_ADVERTISEMENT
                            },
                            {
                                title: i18n.t('tekst Personalizacja (cookie consent)'),
                                description: i18n.t('tekst Pliki cookie używane do personalizacji pomagają usługom zrozumieć Twoje preferencje i zapewniają Ci bardziej personalizowane doświadczenie. (cookie consent)'),
                                linkedCategory: CAT_PERSONALIZATION
                            }
                        ]
                    }
                }
            }
        },
        categories: categories,
        guiOptions: {
            consentModal: {
                layout: 'cloud inline',
                position: 'bottom left',
                equalWeightButtons: true,
                flipButtons: true
            },
            preferencesModal: {
                layout: 'box',
                position: 'right',
                equalWeightButtons: true,
                flipButtons: true
            }
        },

        onFirstConsent: ({cookie}) => {
            executeServiceCallbacks('onFirstConsent', cookie);
        },

        onConsent: ({cookie}) => {
            executeServiceCallbacks('onConsent', cookie);
        },

        onChange: ({cookie, changedCategories, changedServices}) => {
            executeServiceCallbacks('onChange', cookie, changedCategories, changedServices);
        }
    })
});


window.CookieConsentShowPreferences = function () {
    if (typeof window.CookieConsent !== 'undefined') {
        window.CookieConsent.showPreferences()
    }
}
